import React, { useContext, useState, useEffect } from 'react'
import { Redirect, Switch, Route } from 'react-router'
import { AuthContext, AuthState, UserType } from '../../contexts/authContext'
import { VendorContext } from '../../contexts/vendorContext'
import { VendorStatus } from '../../models/Vendor'
import { Spinner } from 'react-bootstrap'
import Layout from '../common/layout/layout'
import styles from './index.module.css'
import { getCompanyProfile } from '../../services/vendorService'
import VendorDashboard from './dashboard/index'
import VendorListingManagement from './listings/index'
import VendorListingAdd from './listings/add'
import VendorListingEdit from './listings/edit'
import Orders from './orders/index'
import OrderDetail from './orders/orderDetail'
import Gifts from './gifts/index'
import CreateGift from './gifts/createGift'
import EditGift from './gifts/updateGift'

const VendorMain: React.FC = () => {
  const authContext = useContext(AuthContext)
  const vendorContext = useContext(VendorContext)
  const [isUnverified, setIsUnverified] = useState(false)

  useEffect(() => {
    const getVendor = async () => {
      if (vendorContext?.companyProfile) {
        return Promise.resolve(vendorContext?.companyProfile)
      }

      if (vendorContext?.profile) {
        const vendor = await getCompanyProfile(vendorContext.profile.vendorId)
        vendorContext?.setCompanyProfile(vendor)
        return vendor
      } else {
        return Promise.resolve(null)
      }
    }

    if (authContext?.authState === AuthState.signedIn) {
      getVendor().then(vendor => {
        if (vendor) {
          if (vendor.status === VendorStatus.unverified) {
            setIsUnverified(true)
          } else {
            setIsUnverified(false)
          }
        } else {
          // @TODO: handle the case where vendor account cannot be retrieved
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authContext?.authState, vendorContext?.profile])

  if (
    authContext?.authState === AuthState.signedIn &&
    authContext?.userType === UserType.migrant
  ) {
    // Use case: Logged in user is migrant
    // Action: redirect to migrant home page
    return (
      <Redirect
        to={{
          pathname: '/'
        }}
      />
    )
  } else if (authContext?.authState === AuthState.signedOut) {
    // Use case: User is not logged in
    // Action: redirect to vendor signin page
    return (
      <Redirect
        to={{
          pathname: '/vendor/login'
        }}
      />
    )
  }

  // Use case: Vendor user is logged in
  // Action: check account status
  //   unknown=show loading page
  //   unverified=show unverified page
  //   else=show target page

  if (!vendorContext?.profile || !vendorContext?.companyProfile) {
    return (
      <div className="modal-loader">
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading your account...</p>
      </div>
    )
  }

  if (isUnverified) {
    return (
      <Layout>
        <div className={styles.unverifiedWrapper}>
          <div className={styles.unverifiedTextBlock}>
            <h2>Verification Required</h2>
            <br />
            <p>Your account has been created and waiting to be verified.</p>
            <p>
              Please allow 24 hours before we can verify your account. You can
              also send us an email at{' '}
              <b>
                <a href="mailto:store@bigmochi.com">store@bigmochi.com</a>
              </b>{' '}
              for urgent concerns.
            </p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <Switch>
        <Route exact path="/vendor" component={VendorDashboard} />
        <Route path="/vendor/listings" component={VendorListingManagement} />
        <Route path="/vendor/addlisting" component={VendorListingAdd} />
        <Route path="/vendor/editlisting/:id" component={VendorListingEdit} />
        <Route path="/vendor/orders/:orderId" component={OrderDetail} />
        <Route path="/vendor/orders" component={Orders} />
        <Route path="/vendor/gifts" component={Gifts} />
        <Route path="/vendor/creategift" component={CreateGift} />
        <Route path="/vendor/editgift/:id" component={EditGift} />
      </Switch>
    </Layout>
  )
}

export default VendorMain
